// Libs
import React, { useContext, useState } from 'react';
// Actions
import { doLogout, setTheme, setLanguage, updateUserInfo } from 'screens/Login/actions';
import { sendToast } from 'screens/Dashboard/actions';
// Hooks
import { useDispatch, useSelector } from 'react-redux';
import useTheme from 'hooks/useTheme';
// Selectors
import { selectProfile } from 'screens/Login/selectors';
// Utils
import { CloudStorage } from 'components/AppConfig.json';
// Services
import { ApiServiceV2 } from 'services';
// Context
import { ModalBuilderContext } from 'core/context';
// Components
import { Wrapper, Modal, Avatar, Button, Tabs, EditIcon, EyeIcon } from 'components';
import { SelectUserProfileActive } from 'components/DataFieldManager';
import { Switch, Input, Upload, Button as AntButton, Select } from 'antd';
import { useTranslation } from 'i18n';

const { Wrap: TabWrap, Pane: TabPane } = Tabs;

const AvatarSection = ({ userId, avatarTs, onUpload, userName, theme }) => (
	<Wrapper width='100%' gap='15px' margin='0' justifyContent='center' flexDirection='column'>
		<div style={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
			<div style={{ zoom: 1.8 }}>
				<Avatar
					fit
					avatar={{
						src: CloudStorage.concat(
							`/syn4pse/users/avatars/${userId ? `${userId}.jpg` : `default.svg`}${avatarTs ? `?t=${avatarTs}` : ''}`,
						),
					}}
				/>
			</div>
			<Upload
				accept='image/jpeg,image/png'
				showUploadList={false}
				beforeUpload={file => {
					onUpload(file);
					return false;
				}}
			>
				<div
					style={{
						position: 'absolute',
						bottom: 4,
						right: 4,
						backgroundColor: 'rgba(0,0,0,0.6)',
						borderRadius: '50%',
						width: 24,
						height: 24,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						cursor: 'pointer',
					}}
				>
					<EditIcon onlyIcon />
				</div>
			</Upload>
		</div>
		{userName && (
			<div style={{ textAlign: 'center' }}>
				<span style={{ color: theme._highEmphasisColor, fontSize: '22px', fontWeight: 600 }}>
					{userName}
				</span>
			</div>
		)}
		<Wrapper padding='0 16px' width='50%' gap='5px' flexDirection='column' justifyContent='center'>
			<SelectUserProfileActive userId={userId} />
		</Wrapper>
	</Wrapper>
);

const LANGUAGE_OPTIONS = [
	{ value: 'es', label: 'Español' },
	{ value: 'en', label: 'English' },
	{ value: 'pt', label: 'Português' },
];

const PerfilTab = ({ userId, userName, theme, currentTheme, toggleLoading, onToggleTheme, avatarTs, onAvatarUpload, onLogout, t, currentLanguage, onChangeLanguage }) => (
	<Wrapper width='100%' margin='0' flexDirection='column' gap='12px'>
		<AvatarSection userId={userId} avatarTs={avatarTs} onUpload={onAvatarUpload} userName={userName} theme={theme} />
		<Wrapper width='100%' margin='0' padding='0 16px' justifyContent='space-between' alignItems='center'>
			<span style={{ color: theme._mediumEmphasisColor, fontSize: '14px' }}>{t.profile.themeLight}</span>
			<Switch checked={currentTheme === 'light'} onChange={onToggleTheme} loading={toggleLoading} />
		</Wrapper>
		{/* Language selector — hidden until fully implemented
		<Wrapper width='100%' margin='0' padding='0 16px' justifyContent='space-between' alignItems='center'>
			<span style={{ color: theme._mediumEmphasisColor, fontSize: '14px' }}>{t.profile.language}</span>
			<Select
				value={currentLanguage}
				onChange={onChangeLanguage}
				size='small'
				style={{ width: 110 }}
				options={LANGUAGE_OPTIONS}
			/>
		</Wrapper>
		*/}
		<Wrapper width='100%' margin='0' justifyContent='center'>
			<Button onClick={onLogout}>{t.profile.logout}</Button>
		</Wrapper>
	</Wrapper>
);

const fieldStyle = {
	display: 'flex',
	flexDirection: 'column',
	gap: 4,
	marginBottom: 10,
	width: '100%',
};

const InformacionTab = ({ profile, theme, onSave, saving, t }) => {
	const [name, setName] = useState(profile?.user?.name || '');
	const [emails, setEmails] = useState(profile?.user?.profile?.emails || []);
	const [phones, setPhones] = useState(profile?.user?.profile?.phones || []);

	const labelStyle = { color: theme._mediumEmphasisColor, fontSize: '12px' };

	const addItem = (setter, list) => setter([...list, '']);
	const removeItem = (setter, list, idx) => setter(list.filter((_, i) => i !== idx));
	const updateItem = (setter, list, idx, val) => setter(list.map((v, i) => (i === idx ? val : v)));

	return (
		<div style={{ padding: '0 16px', width: '100%' }}>
			<div style={fieldStyle}>
				<span style={labelStyle}>{t.profile.name}</span>
				<Input value={name} onChange={e => setName(e.target.value)} />
			</div>

			<div style={fieldStyle}>
				<span style={labelStyle}>{t.profile.emails}</span>
				{emails.map((email, i) => (
					<div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
						<Input value={email} onChange={e => updateItem(setEmails, emails, i, e.target.value)} />
						<AntButton size='small' onClick={() => removeItem(setEmails, emails, i)}>−</AntButton>
					</div>
				))}
				<AntButton size='small' onClick={() => addItem(setEmails, emails)}>{t.profile.addEmail}</AntButton>
			</div>

			<div style={fieldStyle}>
				<span style={labelStyle}>{t.profile.phones}</span>
				{phones.map((phone, i) => (
					<div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
						<Input value={phone} onChange={e => updateItem(setPhones, phones, i, e.target.value)} />
						<AntButton size='small' onClick={() => removeItem(setPhones, phones, i)}>−</AntButton>
					</div>
				))}
				<AntButton size='small' onClick={() => addItem(setPhones, phones)}>{t.profile.addPhone}</AntButton>
			</div>

			<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
				<AntButton type='primary' loading={saving} onClick={() => onSave({ name, emails: emails.filter(Boolean), phones: phones.filter(Boolean) })}>
					{t.profile.save}
				</AntButton>
			</div>
		</div>
	);
};

const SeguridadTab = ({ theme, onSave, saving, t }) => {
	const [open, setOpen] = useState(false);
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [error, setError] = useState('');

	const labelStyle = { color: theme._mediumEmphasisColor, fontSize: '12px' };

	const handleSubmit = () => {
		if (!currentPassword || !newPassword || !confirmPassword) {
			setError(t.profile.fillAllFields);
			return;
		}
		if (newPassword !== confirmPassword) {
			setError(t.profile.passwordMismatch);
			return;
		}
		setError('');
		onSave({ currentPassword, newPassword }, () => {
			setCurrentPassword('');
			setNewPassword('');
			setConfirmPassword('');
			setOpen(false);
		});
	};

	if (!open) {
		return (
			<div style={{ padding: '4px 16px' }}>
				<AntButton onClick={() => setOpen(true)}>{t.profile.changePassword}</AntButton>
			</div>
		);
	}

	const inputStyle = {
		backgroundColor: theme._primaryBackgroundColor,
		color: theme._highEmphasisColor,
	};


	return (
		<div style={{ padding: '0 16px', width: '100%' }}>
			<div style={{ ...fieldStyle }}>
				<span style={labelStyle}>{t.profile.currentPassword}</span>
				<Input.Password
					style={inputStyle}
					value={currentPassword}
					onChange={e => setCurrentPassword(e.target.value)}
					iconRender={visible => (
						<span style={{ display: 'flex', alignItems: 'center', transform: 'scale(0.75)', cursor: 'pointer' }}>
							<EyeIcon fill={visible ? theme._highEmphasisColor : theme._inactiveColor} />
						</span>
					)}
				/>
			</div>
			<div style={{ ...fieldStyle }}>
				<span style={labelStyle}>{t.profile.newPassword}</span>
				<Input.Password
					style={inputStyle}
					value={newPassword}
					onChange={e => setNewPassword(e.target.value)}
					iconRender={visible => (
						<span style={{ display: 'flex', alignItems: 'center', transform: 'scale(0.75)', cursor: 'pointer' }}>
							<EyeIcon fill={visible ? theme._highEmphasisColor : theme._inactiveColor} />
						</span>
					)}
				/>
			</div>
			<div style={{ ...fieldStyle }}>
				<span style={labelStyle}>{t.profile.confirmPassword}</span>
				<Input.Password
					style={inputStyle}
					value={confirmPassword}
					onChange={e => setConfirmPassword(e.target.value)}
					iconRender={visible => (
						<span style={{ display: 'flex', alignItems: 'center', transform: 'scale(0.75)', cursor: 'pointer' }}>
							<EyeIcon fill={visible ? theme._highEmphasisColor : theme._inactiveColor} />
						</span>
					)}
				/>
			</div>
			{error && <div style={{ color: theme._warningColor, fontSize: '12px', marginBottom: 8 }}>{error}</div>}
			<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24 }}>
				<AntButton onClick={() => { setOpen(false); setError(''); }}>{t.profile.cancel}</AntButton>
				<AntButton loading={saving} type='primary' onClick={handleSubmit}>{t.profile.changePassword}</AntButton>
			</div>
		</div>
	);
};

const Profile = () => {
	const {
		payload: { userId },
		cleanModal,
	} = useContext(ModalBuilderContext);

	const dispatch = useDispatch();
	const profile = useSelector(selectProfile);
	const { theme } = useTheme();
	const { t, language } = useTranslation();
	const currentTheme = profile?.user?.settings?.theme || 'dark';

	const [toggleLoading, setToggleLoading] = useState(false);
	const [infoSaving, setInfoSaving] = useState(false);
	const [passwordSaving, setPasswordSaving] = useState(false);
	const [avatarTs, setAvatarTs] = useState(null);

	const logout = () => {
		dispatch(doLogout());
		window.location.reload();
	};

	const userName = profile?.user?.name;

	const onToggleTheme = async checked => {
		const newTheme = checked ? 'light' : 'dark';
		const prevTheme = currentTheme;
		dispatch(setTheme(newTheme));
		setToggleLoading(true);
		try {
			await ApiServiceV2.post({ url: `/users/changeTheme/${userId}`, data: { isDarkTheme: !checked } });
		} catch {
			dispatch(setTheme(prevTheme));
			dispatch(sendToast({ message: t.profile.themeError, type: 'error' }));
		} finally {
			setToggleLoading(false);
		}
	};

	const onChangeLanguage = async newLanguage => {
		const prevLanguage = language;
		dispatch(setLanguage(newLanguage));
		try {
			await ApiServiceV2.post({ url: `/users/changeLanguage/${userId}`, data: { language: newLanguage } });
		} catch {
			dispatch(setLanguage(prevLanguage));
			dispatch(sendToast({ message: t.profile.languageError, type: 'error' }));
		}
	};

	const onAvatarUpload = async file => {
		const formData = new FormData();
		formData.append('photo', file);
		try {
			await ApiServiceV2.post({ url: '/users/profilePhoto', data: formData });
			setAvatarTs(Date.now());
			dispatch(sendToast({ message: t.profile.photoUpdated, type: 'success' }));
		} catch (err) {
			const msg = err?.detail || err?.error || t.profile.photoError;
			dispatch(sendToast({ message: msg, type: 'error' }));
		}
	};

	const onSaveInfo = async ({ name, emails, phones }) => {
		setInfoSaving(true);
		try {
			await ApiServiceV2.put({ url: '/users/profile', data: { name, emails, phones } });
			dispatch(updateUserInfo({ name, emails, phones }));
			dispatch(sendToast({ message: t.profile.infoUpdated, type: 'success' }));
		} catch {
			dispatch(sendToast({ message: t.profile.infoError, type: 'error' }));
		} finally {
			setInfoSaving(false);
		}
	};

	const onChangePassword = async ({ currentPassword, newPassword }, onSuccess) => {
		setPasswordSaving(true);
		try {
			await ApiServiceV2.put({ url: '/users/changePassword', data: { currentPassword, newPassword } });
			onSuccess();
			dispatch(sendToast({ message: t.profile.passwordUpdated, type: 'success' }));
		} catch (err) {
			const msg = err?.error || t.profile.passwordError;
			dispatch(sendToast({ message: msg, type: 'error' }));
		} finally {
			setPasswordSaving(false);
		}
	};

	return (
		<Modal
			title={null}
			closable={false}
			centered={false}
			visible={true}
			onCancel={cleanModal}
			footer={null}
			width={520}
		>
			<Wrapper width='100%' margin='0' justifyContent='center' flexDirection='column'>
				<TabWrap defaultActiveKey='perfil' size='small'>
					<TabPane tab={t.profile.tabs.perfil} key='perfil'>
						<PerfilTab
							userId={userId}
							userName={userName}
							theme={theme}
							currentTheme={currentTheme}
							toggleLoading={toggleLoading}
							onToggleTheme={onToggleTheme}
							avatarTs={avatarTs}
							onAvatarUpload={onAvatarUpload}
							onLogout={logout}
							t={t}
							currentLanguage={language}
							onChangeLanguage={onChangeLanguage}
						/>
					</TabPane>
					<TabPane tab={t.profile.tabs.info} key='info'>
						<InformacionTab
							profile={profile}
							theme={theme}
							onSave={onSaveInfo}
							saving={infoSaving}
							t={t}
						/>
					</TabPane>
					<TabPane tab={t.profile.tabs.security} key='seguridad'>
						<SeguridadTab
							theme={theme}
							onSave={onChangePassword}
							saving={passwordSaving}
							t={t}
						/>
					</TabPane>
				</TabWrap>
			</Wrapper>
		</Modal>
	);
};

export default Profile;
